import { Folder, FolderTree, Languages } from 'lucide-react';
import { Badge, Dialog, formatLanguage, formatNumber, FormSection } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { nameIn, type Category } from './categories.ts';
import styles from './Categories.module.css';

export type CategoryDetailsProps = {
  category: Category;
  parent: Category | null;
  products: number | undefined;
  locales: readonly string[];
  onClose: () => void;
};

const NO_VALUE = '—';

export const CategoryDetails = ({ category, parent, products, locales, onClose }: CategoryDetailsProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const translationIn = (code: string) => category.translations.find((entry) => entry.locale === code);
  const name = nameIn(category, locale, category.id);

  return (
    <Dialog open variant="side" title={name} closeLabel={t('common.close')} onClose={onClose}>
      <div className={styles['details']}>
        <div className={styles['detailsHero']}>
          <span className={styles['detailsIcon']} aria-hidden="true">
            <Folder size={22} />
          </span>
          <div className={styles['detailsHeroText']}>
            <span className={styles['detailsPath']}>
              {parent === null ? name : `${nameIn(parent, locale, parent.id)} › ${name}`}
            </span>
            <strong className={styles['detailsCount']}>
              {products === undefined
                ? NO_VALUE
                : `${formatNumber(products, locale)} ${t('categoryList.productsUnit', { count: products })}`}
            </strong>
          </div>
          <Badge tone={category.status === 'active' ? 'success' : 'neutral'}>
            {t(`categoryList.status_${category.status}`)}
          </Badge>
        </div>
        <FormSection
          variant="plain"
          title={t('categoryList.detailPlacement')}
          description={t('categoryList.detailPlacementHint')}
          icon={<FolderTree size={18} />}
        >
          <dl className={styles['detailsFacts']}>
            <div>
              <dt>{t('categoryList.detailParent')}</dt>
              <dd>{parent === null ? t('categoryList.detailRoot') : nameIn(parent, locale, parent.id)}</dd>
            </div>
            <div>
              <dt>{t('categoryList.detailPosition')}</dt>
              <dd>{formatNumber(category.position, locale)}</dd>
            </div>
          </dl>
        </FormSection>
        <FormSection
          variant="plain"
          title={t('categoryList.detailTranslations')}
          description={t('categoryList.detailTranslationsHint')}
          icon={<Languages size={18} />}
        >
          <ul className={styles['detailsTranslations']}>
            {locales.map((code) => {
              const translation = translationIn(code);
              return (
                <li key={code} lang={code} data-missing={translation === undefined}>
                  <span className={styles['detailsLanguage']}>{formatLanguage(code, locale)}</span>
                  {translation === undefined ? (
                    <Badge tone="warning">{t('categoryList.detailMissing')}</Badge>
                  ) : (
                    <>
                      <strong className={styles['detailsName']}>{translation.name}</strong>
                      <code className={styles['detailsSlug']} dir="ltr">{`/c/${translation.slug}`}</code>
                      <span className={styles['muted']}>
                        {translation.description ?? t('categoryList.detailNoDescription')}
                      </span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </FormSection>
      </div>
    </Dialog>
  );
};
